import React, { useState, useEffect } from 'react';
import { useTaskContext } from '../context/TaskContext';
import ModalWindow from '../components/ModalWindow';
import { useStatusContext } from '../context/StatusContext';
import { useUserContext } from '../context/UserContext';
import { useRolesContext } from '../context/RolesContext';
import { faPlus, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContractorsContext } from '../context/ContractorsContext';

/* ---- Скелетоны ---- */
const SkeletonCard = () => (
  <div className="crm-card crm-card--skeleton">
    <div className="skeleton skeleton--title" />
    <div className="skeleton skeleton--date" />
    <div className="skeleton skeleton--text" style={{ width: '70%' }} />
    <div className="crm-card__footer" style={{ marginTop: 10 }}>
      <div className="skeleton skeleton--badge" />
      <div className="skeleton skeleton--badge" />
    </div>
  </div>
);

const SkeletonColumn = ({ index }) => (
  <div className="crm-column">
    <div
      className="crm-column__header"
      style={{ backgroundColor: index % 2 === 0 ? '#94a3b8' : '#7c3aed', opacity: 0.4 }}
    >
      <div className="skeleton skeleton--col-title" />
      <div className="skeleton skeleton--col-count" />
    </div>
    <div className="crm-column__body">
      {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
    </div>
  </div>
);

const HomePage = ({ externalCreateOpen, onExternalCreateClose }) => {

    const { tasks, fetchTasks, updateTask, tasksLoading } = useTaskContext();
    const {users, role, roleId, isAuthenticated} = useUserContext();
    const { roles } = useRolesContext();
    const { status } = useStatusContext();
    const [selectedTask, setSelectedTask] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [columns, setColumns] = useState([]);
    const { contractors } = useContractorsContext();

    useEffect(() => {
      fetchTasks();
    }, []);

    // Открытие модала при внешнем триггере (кнопка Добавить в сайдбаре)
    useEffect(() => {
      if (externalCreateOpen) {
        openModal(null);
        if (onExternalCreateClose) onExternalCreateClose();
      }
    }, [externalCreateOpen]);

    useEffect(()=>{
      if (roles && roles.length>0){
        let idx = roles.findIndex((role)=>{return role._id === roleId})
        if (idx!==-1){
          if (roles[idx].type === 'admin' || roles[idx].type === 'firstAdmin') {
            setColumns(status)
          } else {
            let c = [];
            for (let i=0; i<status.length; i++) {
              let stat = status[i];
              if (roles[idx].columns&& roles[idx].columns[stat._id]){
                c.push(stat)
              }
            }
            setColumns(c)
          }
        } 
      }
    }, [roles, status])

    const openModal = task => {
      setSelectedTask(task);
      setIsModalOpen(true);
    };

    const closeModal = () => {
      setSelectedTask(null);
      setIsModalOpen(false);
    };

    const upd = (task, update) => {
      let id = task._id
      delete task._id
      updateTask(task, id)
    };

    const getUserName = (userId) => {
      const user = users?.find(u => u._id === userId);
      return user ? user.name : '—';
    };

    const getContractorsName = (id) => {
      const contractor = contractors?.find(u => u._id === id);
      return contractor ? contractor.name : '—';
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hh}:${mm}`;
    };
  
  return (
    <div className="crm-page">
      {/* Breadcrumb */}
      <div className="crm-breadcrumb">
        <span className="crm-breadcrumb__item">Рабочий стол</span>
        <span className="crm-breadcrumb__sep">›</span>
        <span className="crm-breadcrumb__item crm-breadcrumb__item--active">Задачи</span>
      </div>

      {/* Заголовок страницы */}
      <div className="crm-page-header">
        <h1 className="crm-page-title">Задачи</h1>
        <button className="crm-add-btn" onClick={() => openModal(null)}>
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {/* Kanban Board */}
      <div className="crm-board">
        {/* Скелетоны пока колонки ещё не загружены */}
        {tasksLoading || columns.length === 0 ? (
          [0, 1, 2].map((i) => <SkeletonColumn key={i} index={i} />)
        ) : (
          columns.map(column => {
            const columnTasks = tasks?.filter(t => t.status === column._id) || [];
            return (
              <div key={column._id} className="crm-column">
                {/* Заголовок колонки */}
                <div className="crm-column__header" style={{backgroundColor: column.color || '#7c3aed'}}>
                  <span className="crm-column__title">{column.name}</span>
                  <span className="crm-column__count">Количество: {columnTasks.length}</span>
                </div>

                {/* Карточки */}
                <div className="crm-column__body">
                  {columnTasks.map(task => (
                    <div key={task._id} className="crm-card">
                      <div className="crm-card__header">
                        <span className="crm-card__title" onClick={() => openModal(task)}>
                          {task.name}
                        </span>
                        <button className="crm-card__menu-btn" title="Действия">
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                      </div>

                      {task.createdAt && (
                        <div className="crm-card__date">{formatDate(task.createdAt)}</div>
                      )}

                      {task.contractor && (
                        <div className="crm-card__contractor">
                          <span className="crm-card__contractor-link" onClick={() => openModal(task)}>
                            {getContractorsName(task.contractor)}
                          </span>
                        </div>
                      )}

                      <div className="crm-card__footer">
                        {/* <select
                          className="crm-card__executor-select"
                          value={task.executor}
                          onChange={(e) => upd({...task, executor: e.target.value})}
                        >
                          {users?.map(user=>(
                            <option key={user._id} value={user._id}>{user.name}</option>
                          ))}
                        </select> */}
                        <div className="crm-card__executors">
                          {(task.executors || (task.executor ? [task.executor] : [])).map((uid) => (
                            <span key={uid} className="crm-card__executor-badge" title={getUserName(uid)}>
                              {getUserName(uid)}
                            </span>
                          ))}
                        </div>

                        <select
                          className="crm-card__status-select"
                          value={task.status}
                          onChange={(e) => upd({...task, status: e.target.value})}
                        >
                          {status?.map(statu=>(
                            <option key={statu._id} value={statu._id}>{statu.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {isModalOpen && <ModalWindow task={selectedTask} onClose={closeModal}/>}
    </div>
  );
};

export default HomePage;